import {
  ReactElement,
  ChangeEvent,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import {
  createFilterOptions,
  Tooltip,
  TextField,
  Button,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Stack,
  Typography,
  InputAdornment,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown as MoreFiltersIcon,
  faTruckFast as VehicleIcon,
  faVectorSquare as LogisticsZonesIcon,
} from '@fortawesome/free-solid-svg-icons';
import { compact } from 'lodash';

import {
  Subscription,
  CustomAutocomplete,
  VehicleInfo,
  UserInfo,
  AccessFeature,
  LogisiticZoneInfo,
  OrdersFiltersPresets,
} from '~components/index';
import {
  useEnabledCrossCompany,
  useToggleMenu,
  useCurrentCapacityTypeInfo,
} from '~hooks/index';
import {
  ORDERS_PENDING_STATE_OPTIONS,
  ORDERS_ON_AGENDA_STATE_OPTIONS,
  ORDERS_FINALIZED_STATE_OPTIONS,
} from '~constants/ordersStatesOptions';
import { RoutesActionFilterChip } from '~modules/Routes/styles';
import { getDefaultAvatar } from '~utils/user';
import {
  getSuffixCapacity,
  getDescriptionCapacity,
} from '~utils/capacityTypes';
import { Nullable } from '~globals/types';
import { PlanFeaturesTypes, Roles } from '~globals/types/enums';
import { ItemPresetParse } from '~services/item/types';

import { OrdersFiltersMenuProps } from './types';
import {
  OrdersFiltersMenuContent,
  OrdersFiltersMenuInputs,
  OrdersFiltersMenuActions,
  OrdersFiltersMenuIconButton,
  OrdersFiltersMenuIconButtonIcon,
} from './styles';

const OrdersFiltersMenu = ({
  type,
  filters,
  activeFilters,
  onChangeFilterInput,
  onResetFilters,
  onApplyFilters,
  onClearFilters,
  selectsData,
  presets,
}: OrdersFiltersMenuProps): ReactElement => {
  const { unit1CapacityTypeId, unit2CapacityTypeId } =
    useCurrentCapacityTypeInfo();

  const { isEnabledCrossCompany } = useEnabledCrossCompany();

  const [anchorMenuEl, openMenu, , handleToggleOpenMenu] = useToggleMenu();

  const [showTooltip, setShowTooltip] = useState(false);

  const handleShowTooltip = useCallback(() => {
    if (!openMenu) {
      setShowTooltip(true);
    }
  }, [openMenu]);

  useEffect(() => {
    if (openMenu) {
      setShowTooltip(false);
    }
  }, [openMenu]);

  const setAppliedFilterByKey = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (fieldName: string, newVal: any) => {
      onChangeFilterInput(fieldName, newVal);
    },
    [onChangeFilterInput],
  );

  const handleChangeInputFilter = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = event.target ?? event.currentTarget;

      setAppliedFilterByKey(name, value);
    },
    [setAppliedFilterByKey],
  );

  const handleChangeInputDateFilter = useCallback(
    (fieldName: string) =>
      (value: Date | null): void => {
        setAppliedFilterByKey(fieldName, value);
      },
    [setAppliedFilterByKey],
  );

  const handleApplyFilter = useCallback(() => {
    onApplyFilters();
    handleToggleOpenMenu(false);
  }, [onApplyFilters, handleToggleOpenMenu]);

  const handleClearFilters = useCallback(() => {
    onClearFilters();
    presets?.selectedPreset.reset();
  }, [onClearFilters, presets?.selectedPreset]);

  const handleClose = useCallback(() => {
    handleToggleOpenMenu(false);

    onResetFilters();
  }, [handleToggleOpenMenu, onResetFilters]);

  const isValidToApply = useMemo(
    () => !(JSON.stringify(filters) === JSON.stringify(activeFilters)),
    [filters, activeFilters],
  );

  const isEmptyFilters = useMemo(() => {
    const currentFilters = [
      filters.search,
      filters.expirationDate,
      filters.itemState?.toString(),
      filters.dateFrom,
      filters.dateTo,
      filters.state?.toString(),
      filters.vehicles?.length,
      filters.drivers?.length,
      filters.services?.length,
      filters.zones?.length,
      filters.unit1Gt,
      filters.unit1Lt,
      filters.unit2Gt,
      filters.unit2Lt,
      filters.priceGt,
      filters.priceLt,
      filters.assignedUser,
    ];

    const compactCurrentFilters = compact(currentFilters);

    return compactCurrentFilters.length === 0;
  }, [filters]);

  const routeItemStatesOptions = useMemo(() => {
    if (type === 'finalized') {
      return ORDERS_FINALIZED_STATE_OPTIONS;
    }

    return ORDERS_ON_AGENDA_STATE_OPTIONS;
  }, [type]);

  const handleChangePreset = useCallback(
    (currentPreset: Nullable<ItemPresetParse>) => {
      if (presets) {
        presets.selectedPreset.handleChange(currentPreset);

        if (currentPreset) {
          handleToggleOpenMenu(false);
        }
      }
    },
    [handleToggleOpenMenu, presets],
  );

  const handleGenericSuccessPresetDialog = useCallback(
    (currentPreset: ItemPresetParse) => {
      if (presets) {
        handleToggleOpenMenu(false);
        presets.handleApplyFiltersByPreset(currentPreset);
        presets.reload();
      }
    },
    [presets, handleToggleOpenMenu],
  );

  const handleConfirDeletePresetDialog = useCallback(() => {
    if (presets) {
      presets.selectedPreset.reset();
      presets.reload();
    }
  }, [presets]);

  return (
    <>
      <Tooltip
        title="Mas filtros"
        arrow
        open={showTooltip}
        onOpen={handleShowTooltip}
        onClose={() => setShowTooltip(false)}
      >
        <OrdersFiltersMenuIconButton
          color="primary"
          variant="contained"
          ref={anchorMenuEl}
          aria-label="orders-filters"
          aria-controls={openMenu ? 'orders-filters-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={openMenu ? 'true' : undefined}
          onClick={() => handleToggleOpenMenu(true)}
        >
          <OrdersFiltersMenuIconButtonIcon icon={MoreFiltersIcon} />
        </OrdersFiltersMenuIconButton>
      </Tooltip>

      <Menu
        id="orders-filters-menu"
        anchorEl={anchorMenuEl.current}
        open={openMenu}
        onClose={handleClose}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'center', vertical: 'top' }}
        keepMounted
      >
        <OrdersFiltersMenuContent>
          <OrdersFiltersMenuInputs>
            {type === 'pending' && (
              <>
                <Stack spacing={0.5}>
                  <AccessFeature.Typograghy
                    validFeature={PlanFeaturesTypes.LimitNotPro}
                    variant="caption"
                    color="text.secondary"
                  >
                    &#8288;{getDescriptionCapacity(unit1CapacityTypeId)}
                  </AccessFeature.Typograghy>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <AccessFeature.TextField
                      validFeature={PlanFeaturesTypes.LimitNotPro}
                      showChip={false}
                      label="Mayor a"
                      name="unit1Gt"
                      value={filters.unit1Gt ?? ''}
                      onChange={handleChangeInputFilter}
                      type="number"
                      onKeyDown={(event) => event.stopPropagation()}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {getSuffixCapacity(unit1CapacityTypeId)}
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Typography
                      variant="h6"
                      color="text.secondary"
                      fontWeight={400}
                    >
                      -
                    </Typography>

                    <AccessFeature.TextField
                      validFeature={PlanFeaturesTypes.LimitNotPro}
                      showChip={false}
                      label="Menor a"
                      name="unit1Lt"
                      value={filters.unit1Lt ?? ''}
                      onChange={handleChangeInputFilter}
                      type="number"
                      onKeyDown={(event) => event.stopPropagation()}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {getSuffixCapacity(unit1CapacityTypeId)}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>
                </Stack>

                <Stack spacing={0.5}>
                  <AccessFeature.Typograghy
                    validFeature={PlanFeaturesTypes.LimitNotPro}
                    variant="caption"
                    color="text.secondary"
                  >
                    &#8288;{getDescriptionCapacity(unit2CapacityTypeId)}
                  </AccessFeature.Typograghy>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <AccessFeature.TextField
                      validFeature={PlanFeaturesTypes.LimitNotPro}
                      showChip={false}
                      label="Mayor a"
                      name="unit2Gt"
                      value={filters.unit2Gt ?? ''}
                      onChange={handleChangeInputFilter}
                      type="number"
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {getSuffixCapacity(unit2CapacityTypeId)}
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Typography
                      variant="h6"
                      color="text.secondary"
                      fontWeight={400}
                    >
                      -
                    </Typography>

                    <AccessFeature.TextField
                      validFeature={PlanFeaturesTypes.LimitNotPro}
                      showChip={false}
                      label="Menor a"
                      name="unit2Lt"
                      value={filters.unit2Lt ?? ''}
                      onChange={handleChangeInputFilter}
                      type="number"
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {getSuffixCapacity(unit2CapacityTypeId)}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>
                </Stack>
              </>
            )}

            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">
                &#8288;Precio
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  label="Mayor a"
                  name="priceGt"
                  value={filters.priceGt ?? ''}
                  onChange={handleChangeInputFilter}
                  type="number"
                  onKeyDown={(event) => event.stopPropagation()}
                  fullWidth
                />

                <Typography
                  variant="h6"
                  color="text.secondary"
                  fontWeight={400}
                >
                  -
                </Typography>

                <TextField
                  label="Menor a"
                  name="priceLt"
                  value={filters.priceLt ?? ''}
                  onChange={handleChangeInputFilter}
                  type="number"
                  onKeyDown={(event) => event.stopPropagation()}
                  fullWidth
                />
              </Stack>
            </Stack>

            {(type === 'pending' || type === 'onAgenda') && (
              <Subscription.DataRetentionMin.DatePicker
                label="Fecha de vencimiento"
                value={filters.expirationDate}
                onChange={handleChangeInputDateFilter('expirationDate')}
                renderInput={(props) => <TextField {...props} />}
              />
            )}

            {type === 'pending' && isEnabledCrossCompany && (
              <TextField
                label="Estado"
                name="itemState"
                value={filters.itemState ?? ''}
                onChange={handleChangeInputFilter}
                select
              >
                {ORDERS_PENDING_STATE_OPTIONS.map((state) => (
                  <MenuItem
                    key={`item-state-${state.value}`}
                    value={state.value}
                  >
                    {state.label}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {(type === 'onAgenda' || type === 'finalized') && (
              <>
                <Subscription.DataRetentionMin.DatePicker
                  label="Desde"
                  value={filters.dateFrom ?? null}
                  onChange={handleChangeInputDateFilter('dateFrom')}
                  renderInput={(props) => <TextField {...props} />}
                />

                <Subscription.DataRetentionMin.DatePicker
                  label="Hasta"
                  value={filters.dateTo ?? null}
                  onChange={handleChangeInputDateFilter('dateTo')}
                  renderInput={(props) => <TextField {...props} />}
                />

                <TextField
                  label="Estado"
                  name="state"
                  value={filters.state ?? ''}
                  onChange={handleChangeInputFilter}
                  select
                >
                  {routeItemStatesOptions.map((state) => (
                    <MenuItem
                      key={`route-item-state-${state.value}`}
                      value={state.value}
                    >
                      {state.label}
                    </MenuItem>
                  ))}
                </TextField>

                {selectsData?.vehicles && (
                  <CustomAutocomplete
                    innerRef={selectsData.vehicles.autocompleteRef}
                    multiple
                    disableCloseOnSelect
                    filterSelectedOptions
                    limitTags={1}
                    renderTags={(vals, getTagsProps) =>
                      vals.map((val, index) => (
                        // eslint-disable-next-line react/jsx-key
                        <RoutesActionFilterChip
                          label={`${val?.displayName}${
                            val?.code ? ` ${val.code}` : ''
                          }`}
                          icon={
                            <FontAwesomeIcon
                              icon={VehicleIcon}
                              style={{ color: val.color }}
                            />
                          }
                          size="small"
                          {...getTagsProps({ index })}
                        />
                      ))
                    }
                    disableClearable
                    options={selectsData.vehicles.list}
                    loading={selectsData.vehicles.loading}
                    label="Vehículos"
                    name="vehicles"
                    onChange={(_e, vals) => {
                      setAppliedFilterByKey('vehicles', vals);
                    }}
                    getOptionLabel={(option) =>
                      `${option?.displayName}${
                        option?.code ? ` ${option.code}` : ''
                      }`
                    }
                    renderOption={(props, option) => (
                      <li {...props}>
                        <VehicleInfo
                          vehicle={option.displayName}
                          vehicleCode={option.code as string}
                          color={option.color}
                        />
                      </li>
                    )}
                    sx={{ width: 318 }}
                  />
                )}

                {selectsData?.drivers && (
                  <CustomAutocomplete
                    innerRef={selectsData.drivers.autocompleteRef}
                    multiple
                    disableCloseOnSelect
                    filterSelectedOptions
                    limitTags={1}
                    renderTags={(vals, getTagsProps) =>
                      vals.map((val, index) => (
                        // eslint-disable-next-line react/jsx-key
                        <RoutesActionFilterChip
                          avatar={
                            <Avatar
                              src={getDefaultAvatar(val.avatar ?? undefined)}
                              alt={val.displayName}
                            />
                          }
                          label={val.displayName}
                          size="small"
                          {...getTagsProps({ index })}
                        />
                      ))
                    }
                    disableClearable
                    options={selectsData.drivers.list}
                    loading={selectsData.drivers.loading}
                    label="Choferes"
                    name="drivers"
                    onChange={(_e, vals) => {
                      setAppliedFilterByKey('drivers', vals);
                    }}
                    getOptionLabel={(option) => option.displayName}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <UserInfo
                          name={option.displayName}
                          urlAvatar={getDefaultAvatar(
                            option.avatar ?? undefined,
                          )}
                        />
                      </li>
                    )}
                    filterOptions={createFilterOptions({
                      ignoreAccents: true,
                      ignoreCase: true,
                      stringify: (option) =>
                        option.displayName.replace(',', ''),
                    })}
                    sx={{ width: 318 }}
                  />
                )}
              </>
            )}

            {selectsData?.services && (
              <AccessFeature.AutoComplete
                validFeature={PlanFeaturesTypes.LimitNotPro}
                innerRef={selectsData.services.autocompleteRef}
                multiple
                disableCloseOnSelect
                filterSelectedOptions
                limitTags={1}
                renderTags={(vals, getTagsProps) =>
                  vals.map((val, index) => (
                    // eslint-disable-next-line react/jsx-key
                    <RoutesActionFilterChip
                      label={val.label.toString()}
                      size="small"
                      {...getTagsProps({ index })}
                    />
                  ))
                }
                disableClearable
                options={selectsData.services.list}
                loading={selectsData.services.loading}
                label="Tipos de servicios"
                name="services"
                onChange={(_e, vals) => {
                  setAppliedFilterByKey('services', vals);
                }}
                fullWidth
                getOptionLabel={(option) => option.label.toString()}
              />
            )}

            {selectsData?.logisticsZones && (
              <AccessFeature.AutoComplete
                validFeature={PlanFeaturesTypes.MgmZones}
                innerRef={selectsData.logisticsZones.autocompleteRef}
                multiple
                disableCloseOnSelect
                filterSelectedOptions
                limitTags={1}
                renderTags={(vals, getTagsProps) =>
                  vals.map((val, index) => (
                    // eslint-disable-next-line react/jsx-key
                    <RoutesActionFilterChip
                      icon={
                        val.color ? (
                          <FontAwesomeIcon
                            icon={LogisticsZonesIcon}
                            style={{ color: val.color }}
                          />
                        ) : undefined
                      }
                      label={val.code}
                      size="small"
                      {...getTagsProps({ index })}
                    />
                  ))
                }
                disableClearable
                options={selectsData.logisticsZones.list}
                loading={selectsData.logisticsZones.loading}
                label="Zonas logísticas"
                name="zones"
                onChange={(_e, vals) => {
                  setAppliedFilterByKey('zones', vals);
                }}
                getOptionLabel={(option) =>
                  option.description
                    ? `${option.code} ${option.description}`
                    : option.code
                }
                renderOption={(props, option) => (
                  <li {...props}>
                    <LogisiticZoneInfo
                      name={option.code}
                      description={option.description}
                      color={option.color}
                      withTooltip={Boolean(option.color)}
                    />
                  </li>
                )}
                fullWidth
              />
            )}

            {selectsData?.users && (
              <AccessFeature.AutoComplete
                validFeature={PlanFeaturesTypes.LimitNotPro}
                innerRef={selectsData.users.autocompleteRef}
                options={selectsData.users.list}
                loading={selectsData.users.loading}
                label="Usuario asignado"
                name="assignedUser"
                onChange={(_e, val) => {
                  setAppliedFilterByKey('assignedUser', val);
                }}
                fullWidth
                getOptionLabel={(option) =>
                  `${option.lastName}, ${option.firstName}`
                }
                renderOption={(props, option) => (
                  <li
                    {...props}
                    key={option.userId}
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <UserInfo
                      name={`${option.lastName}, ${option.firstName}`}
                      urlAvatar={option.avatarUrl || undefined}
                    />
                  </li>
                )}
                startAdornment={(val) =>
                  val && (
                    <Avatar
                      src={getDefaultAvatar(val.avatarUrl || undefined)}
                      alt={`${val.lastName}, ${val.firstName}`}
                      sx={{ width: 20, height: 20 }}
                    />
                  )
                }
                groupBy={(option) => {
                  if (option.roleId === Roles.Drive) {
                    return 'Choferes';
                  }

                  return 'Usuarios';
                }}
                filterOptions={createFilterOptions({
                  ignoreAccents: true,
                  ignoreCase: true,
                  stringify: (option) =>
                    `${option.lastName}, ${option.firstName}`.replace(',', ''),
                })}
              />
            )}
          </OrdersFiltersMenuInputs>

          <OrdersFiltersMenuActions>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleApplyFilter}
              disabled={!isValidToApply}
            >
              Aplicar
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={handleClearFilters}
              disabled={isEmptyFilters}
            >
              Borrar Filtros
            </Button>
          </OrdersFiltersMenuActions>

          {type === 'pending' && presets && (
            <>
              <Divider />

              <OrdersFiltersPresets
                options={presets.data}
                loading={presets.loading}
                currentFilters={filters}
                selectedPreset={presets.selectedPreset.value}
                onChangePreset={handleChangePreset}
                actionsDialogs={{
                  create: { onSuccess: handleGenericSuccessPresetDialog },
                  confirmEdit: { onSuccess: handleGenericSuccessPresetDialog },
                  confirmDelete: { onSuccess: handleConfirDeletePresetDialog },
                }}
              />
            </>
          )}
        </OrdersFiltersMenuContent>
      </Menu>
    </>
  );
};

export default OrdersFiltersMenu;
